<template>
    <div class="team-details-container">
        <!-- Initial Page Loading Overlay -->
        <v-overlay :value="initialLoading" z-index="999">
            <div class="d-flex flex-column align-center">
                <v-progress-circular indeterminate size="64" color="primary" width="6"></v-progress-circular>
                <div class="mt-4 text-h6 white--text">Loading Team...</div>
            </div>
        </v-overlay>

        <!-- Back Button -->
        <v-btn text color="primary" @click="goBack" class="back-to-overview-btn mb-4">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Overview
        </v-btn>

        <!-- Header Section -->
        <div class="header-section">
            <div class="team-header">
                <!-- Loading State -->
                <div v-if="loadingTeam" style="width: 100%;">
                    <div class="d-flex align-center mb-2">
                        <v-skeleton-loader type="avatar" width="48" height="48" class="mr-3"></v-skeleton-loader>
                        <div style="flex: 1;">
                            <v-skeleton-loader type="heading" width="300"></v-skeleton-loader>
                        </div>
                    </div>
                    <v-skeleton-loader type="text" width="400" class="mt-2"></v-skeleton-loader>
                </div>

                <!-- Loaded State -->
                <template v-else>
                    <div class="team-info">
                        <div class="d-flex align-center mb-2">
                            <v-avatar class="mr-3" size="48" :color="getTeamColor()">
                                <v-icon dark size="28">{{ getTeamIcon() }}</v-icon>
                            </v-avatar>
                            <div>
                                <h1 class="team-name">{{ teamName }}</h1>
                                <p class="team-subtitle mb-0">Team Dashboard</p>
                            </div>
                        </div>
                    </div>
                    <div class="header-actions">
                        <v-btn color="error" outlined :loading="sendingOverdueReminders" @click="sendOverdueReminders" class="mr-2">
                            <v-icon left small>mdi-email-alert-outline</v-icon>
                            Send Reminders on Backlogs
                        </v-btn>
                        <v-btn color="deep-purple" dark @click="openFeedbackDialog" class="mr-2 text-none">
                            <v-icon left small>mdi-message-star-outline</v-icon>
                            Give Feedback
                        </v-btn>
                        <v-btn color="primary" outlined @click="openManagerDialog" class="mr-2">
                            <v-icon left small>mdi-account-tie</v-icon>
                            {{ managerUsers.length > 0 ? 'Edit Managers' : 'Assign Managers' }}
                        </v-btn>
                    </div>
                </template>
            </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-section">
            <div class="stats-grid">
                <div class="stat-card">
                    <v-icon class="stat-icon" color="#4285F4">mdi-clipboard-text</v-icon>
                    <div class="stat-value">{{ toDoTasks }}</div>
                    <div class="stat-label">To Do</div>
                </div>

                <div class="stat-card">
                    <v-icon class="stat-icon" color="#FFC605">mdi-progress-clock</v-icon>
                    <div class="stat-value">{{ inProgressTasks }}</div>
                    <div class="stat-label">In Progress</div>
                </div>

                <div class="stat-card">
                    <v-icon class="stat-icon" color="#f44336">mdi-alert-octagon</v-icon>
                    <div class="stat-value">{{ blockedTasks }}</div>
                    <div class="stat-label">Blocked</div>
                </div>

                <div class="stat-card">
                    <v-icon class="stat-icon" color="#4CAF50">mdi-check-circle</v-icon>
                    <div class="stat-value">{{ completedTasks }}</div>
                    <div class="stat-label">Completed</div>
                </div>
            </div>
        </div>

        <!-- Tabs Section -->
        <v-card class="tabs-container mb-4">
            <v-tabs v-model="activeTab" background-color="white" color="primary" show-arrows>
                <v-tab>
                    <v-icon left small>mdi-clipboard-text-multiple</v-icon>
                    Tasks
                    <v-chip small class="ml-2" color="primary" outlined>{{ totalTasks }}</v-chip>
                </v-tab>
                <v-tab>
                    <v-icon left small>mdi-account-group</v-icon>
                    Members
                    <v-chip small class="ml-2" color="primary" outlined>{{ totalMembers }}</v-chip>
                </v-tab>
            </v-tabs>
        </v-card>

        <!-- Tab Content -->
        <v-tabs-items v-model="activeTab">
            <!-- Tasks Tab -->
            <v-tab-item>
                <div class="tasks-section">
            <div class="section-header mb-4">
                <h2 class="section-title">Team Tasks</h2>
                <div class="d-flex align-center">
                    <v-btn color="primary" small @click="openCreateTaskDialog" class="mr-2">
                        <v-icon left small>mdi-plus</v-icon>
                        Add Task
                    </v-btn>
                    <v-btn color="primary" outlined small @click="refreshTasks" :loading="loadingTasks" class="mr-2">
                        <v-icon left small>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                    <span class="task-count-badge">{{ filteredTasks.length }} / {{ totalTasks }} tasks</span>
                </div>
            </div>

            <!-- Filters -->
            <div class="task-controls mt-4 mb-4">
                <v-row>
                    <v-col cols="12" sm="6" md="3">
                        <v-autocomplete
                            v-model="statusFilters"
                            :items="statusFilterOptions"
                            item-text="name"
                            item-value="id"
                            label="Status"
                            dense
                            outlined
                            multiple
                            chips
                            :disabled="loadingTasks"
                        >
                            <template v-slot:prepend-inner>
                                <v-icon color="primary" size="16">mdi-format-list-checks</v-icon>
                            </template>
                            <template v-slot:selection="{ item, index }">
                                <v-chip v-if="index < 2" small close @click:close="statusFilters.splice(statusFilters.indexOf(item.id), 1)">
                                    {{ item.name }}
                                </v-chip>
                                <span v-if="index === 2" class="text-grey text-caption">
                                    (+{{ statusFilters.length - 2 }} others)
                                </span>
                            </template>
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-text-field
                            v-model="filters.search"
                            label="Search tasks..."
                            prepend-inner-icon="mdi-magnify"
                            dense
                            outlined
                            clearable
                            :disabled="loadingTasks"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-autocomplete
                            v-model="filters.priority"
                            :items="priorityFilterOptions"
                            item-text="name"
                            item-value="id"
                            label="Priority"
                            dense
                            outlined
                            clearable
                            :disabled="loadingTasks"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-select
                            v-model="filters.dueDate"
                            :items="dueDateFilters"
                            item-text="name"
                            item-value="value"
                            label="Due Date"
                            dense
                            outlined
                            clearable
                            :disabled="loadingTasks"
                        >
                            <template v-slot:prepend-inner>
                                <v-icon color="primary" size="16">mdi-calendar</v-icon>
                            </template>
                        </v-select>
                    </v-col>
                </v-row>
            </div>

            <!-- Loading State -->
            <div v-if="loadingTasks" class="text-center pa-12">
                <v-progress-circular indeterminate size="64" color="primary" width="4"></v-progress-circular>
                <div class="mt-4 text-h6 grey--text">Loading tasks...</div>
            </div>

            <!-- Task Table with Virtual Scrolling -->
            <div v-else-if="filteredTasks.length > 0" class="virtual-scroll-container">
                <UnifiedTreeViewDashboard
                    :tasks="filteredTasks"
                    :users="users()"
                    :companies="companies()"
                    :can-edit-tasks="true"
                    :role-id="roleId"
                    task-type="role_overview"
                    @getTasks="refreshTasks"
                />
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
                <v-icon size="64" color="#E0E0E0">mdi-clipboard-text-outline</v-icon>
                <h3 class="empty-title">No tasks found</h3>
                <p class="empty-description">{{ teamTasks.length === 0 ? 'Create your first task to get started' : 'Try adjusting your filters' }}</p>
                <v-btn v-if="teamTasks.length === 0" color="primary" @click="openCreateTaskDialog">
                    <v-icon left>mdi-plus</v-icon>
                    Add Task
                </v-btn>
            </div>
                </div>
            </v-tab-item>

            <!-- Members Tab -->
            <v-tab-item>
                <div class="members-section">
                    <!-- Managers Section -->
                    <v-card class="mb-4" outlined>
                        <v-card-title class="pb-2">
                            <v-icon left color="primary">mdi-account-tie</v-icon>
                            <span>Managers</span>
                            <v-spacer></v-spacer>
                            <v-btn small color="primary" outlined @click="openManagerDialog">
                                <v-icon left small>mdi-pencil</v-icon>
                                Edit
                            </v-btn>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text>
                            <div v-if="managerUsers.length === 0" class="text-center py-8">
                                <v-icon size="48" color="grey lighten-1">mdi-account-tie-outline</v-icon>
                                <div class="mt-3 grey--text">No managers assigned yet</div>
                                <v-btn small color="primary" class="mt-3" @click="openManagerDialog">
                                    <v-icon left small>mdi-plus</v-icon>
                                    Assign Managers
                                </v-btn>
                            </div>
                            <v-list v-else class="transparent">
                                <v-list-item v-for="manager in managerUsers" :key="manager.id">
                                    <v-list-item-avatar color="primary">
                                        <span class="white--text text-subtitle-2">{{ getInitials(manager.name) }}</span>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ manager.name }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ manager.email }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-chip small color="primary" outlined>
                                            <v-icon left x-small>mdi-shield-account</v-icon>
                                            Manager
                                        </v-chip>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </v-card-text>
                    </v-card>

                    <!-- Team Members Section -->
                    <v-card outlined>
                        <v-card-title class="pb-2">
                            <v-icon left color="primary">mdi-account-multiple</v-icon>
                            <span>Team Members</span>
                            <v-spacer></v-spacer>
                            <v-chip small color="primary" text-color="white">{{ roleMembers.length }} members</v-chip>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text>
                            <div v-if="roleMembers.length === 0" class="text-center py-8">
                                <v-icon size="48" color="grey lighten-1">mdi-account-multiple-outline</v-icon>
                                <div class="mt-3 grey--text">No team members found</div>
                            </div>
                            <v-list v-else class="transparent">
                                <v-list-item v-for="member in roleMembers" :key="member.id">
                                    <v-list-item-avatar :color="isManager(member.id) ? 'primary' : 'grey lighten-1'">
                                        <span class="white--text text-subtitle-2">{{ getInitials(member.name) }}</span>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ member.name }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ member.email }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-chip v-if="isManager(member.id)" small color="primary" outlined>
                                            <v-icon left x-small>mdi-shield-account</v-icon>
                                            Manager
                                        </v-chip>
                                        <v-chip v-else small outlined>
                                            <v-icon left x-small>mdi-account</v-icon>
                                            Member
                                        </v-chip>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </v-card-text>
                    </v-card>
                </div>
            </v-tab-item>
        </v-tabs-items>

        <!-- Task Creation Dialog -->
        <TaskCreationDialog 
            v-model="createTaskDialog" 
            :users="users()" 
            :companies="companies()" 
            :default-role-id="roleId"
            :default-status-id="1"
            task-type="assign"
            @tasks-created="onTasksCreated" 
            @close="createTaskDialog = false" 
        />

        <!-- Manager Assignment Dialog -->
        <v-dialog v-model="managerDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="headline">
                    <v-icon left>mdi-account-tie</v-icon>
                    Assign Managers
                    <v-spacer></v-spacer>
                    <v-btn icon @click="managerDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-6">
                    <v-autocomplete
                        v-model="selectedManagerIds"
                        :items="users()"
                        item-text="name"
                        item-value="id"
                        label="Select Managers"
                        outlined
                        multiple
                        chips
                        deletable-chips
                        clearable
                        prepend-inner-icon="mdi-account-search"
                    >
                        <template v-slot:selection="{ item }">
                            <v-chip small color="primary" text-color="white" close class="ma-1" @click:close="removeManager(item.id)">
                                <v-avatar left small>
                                    <span style="font-size: 10px;">{{ getInitials(item.name) }}</span>
                                </v-avatar>
                                {{ item.name }}
                            </v-chip>
                        </template>
                    </v-autocomplete>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="managerDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :disabled="selectedManagerIds.length === 0" @click="saveManagers">
                        <v-icon left small>mdi-check</v-icon>
                        Assign Managers
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Give Feedback to Team Member Dialog -->
        <v-dialog v-model="feedbackDialog" max-width="560px" persistent>
            <v-card>
                <v-card-title class="headline d-flex align-center">
                    <v-icon left color="deep-purple">mdi-message-star-outline</v-icon>
                    <span>Give Feedback</span>
                    <v-chip small color="deep-purple" dark class="ml-2 font-weight-bold">
                        <v-icon x-small class="mr-1">mdi-account-group</v-icon>
                        {{ teamName }}
                    </v-chip>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <div class="caption grey--text mb-4">
                        Share direct, attributed feedback with a member of <strong>{{ teamName }}</strong>. They'll see it in their Feedback Inbox.
                    </div>

                    <div class="body-2 font-weight-medium mb-1">Team Member</div>
                    <v-autocomplete
                        v-model="feedbackForm.recipient_id"
                        :items="teamMemberOptions"
                        item-text="text"
                        item-value="value"
                        placeholder="Search for a team member..."
                        dense outlined hide-details clearable
                        class="mb-4 rounded-lg"
                        :disabled="sendingFeedback"
                    />

                    <div class="body-2 font-weight-medium mb-1">Category</div>
                    <v-select
                        v-model="feedbackForm.category"
                        :items="feedbackCategories"
                        dense outlined hide-details
                        class="mb-4 rounded-lg"
                        :disabled="sendingFeedback"
                    />

                    <div class="body-2 font-weight-medium mb-1">Your Feedback</div>
                    <v-textarea
                        v-model="feedbackForm.message"
                        placeholder="Specific, kind, actionable..."
                        dense outlined hide-details rows="5" no-resize
                        class="rounded-lg"
                        :disabled="sendingFeedback"
                    />
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeFeedbackDialog" :disabled="sendingFeedback">Cancel</v-btn>
                    <v-btn
                        color="deep-purple" dark
                        :loading="sendingFeedback"
                        :disabled="!feedbackForm.recipient_id || !feedbackForm.message.trim()"
                        @click="sendFeedback"
                    >
                        <v-icon left small>mdi-send</v-icon>
                        Send Feedback
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import UnifiedTreeViewDashboard from "../modules/UnifiedTreeViewDashboard.vue";
import TaskCreationDialog from "../modules/TaskCreationDialog.vue";

export default {
    name: "TeamDetailsComponent",
    inject: [
        'users',
        'companies',
        'allManagers',
        'roleManagers',
        'refreshManagers',
        'statuses',
        'priorities',
    ],
    components: {
        UnifiedTreeViewDashboard,
        TaskCreationDialog,
    },
    data() {
        return {
            initialLoading: true,
            loadingTeam: true,
            loadingTasks: true,
            sendingOverdueReminders: false,
            teamTasks: [],
            teamName: '',
            teamManagers: [],
            createTaskDialog: false,
            managerDialog: false,
            selectedManagerIds: [],
            feedbackDialog: false,
            sendingFeedback: false,
            feedbackForm: { recipient_id: null, category: 'General', message: '' },
            feedbackCategories: ['General', 'Recognition', 'Performance', 'Growth & Development', 'Process', 'Team Dynamics', 'Leadership', 'Workload'],
            activeTab: 0,
            statusFilters: [],
            filters: {
                search: '',
                priority: null,
                dueDate: null,
            },
            dueDateFilters: [
                { name: 'Today', value: 'today' },
                { name: 'This Week', value: 'week' },
                { name: 'This Month', value: 'month' },
                { name: 'Overdue', value: 'overdue' },
                { name: 'No Due Date', value: 'none' },
            ],
        };
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_roles: "auth/get_roles",
        }),
        roleId() {
            return parseInt(this.$route.params.roleId);
        },
        statusFilterOptions() {
            const statusesList = this.statuses();
            if (!Array.isArray(statusesList)) return [];
            return statusesList.map(status => ({
                id: status.id,
                name: status.name,
            }));
        },
        priorityFilterOptions() {
            const prioritiesList = this.priorities();
            if (!Array.isArray(prioritiesList)) return [];
            return [
                { id: null, name: 'All Priorities' },
                ...prioritiesList.map(priority => ({
                    id: priority.id,
                    name: priority.name,
                }))
            ];
        },
        filteredTasks() {
            let filtered = [...this.teamTasks];

            // Status filter
            if (this.statusFilters.length > 0) {
                filtered = filtered.filter(task => this.statusFilters.includes(task.status_id));
            }

            // Search filter
            if (this.filters.search && this.filters.search.trim()) {
                const searchTerm = this.filters.search.toLowerCase();
                filtered = filtered.filter(task => {
                    return (
                        (task.name && task.name.toLowerCase().includes(searchTerm)) ||
                        (task.description && task.description.toLowerCase().includes(searchTerm))
                    );
                });
            }

            // Priority filter
            if (this.filters.priority) {
                filtered = filtered.filter(task => task.priority_id === this.filters.priority);
            }

            // Due date filter
            if (this.filters.dueDate) {
                const today = this.$date();
                filtered = filtered.filter(task => {
                    if (this.filters.dueDate === 'today') {
                        return task.due_date && this.$date(task.due_date).isSame(today, 'day');
                    } else if (this.filters.dueDate === 'week') {
                        return task.due_date && this.$date(task.due_date).isSame(today, 'week');
                    } else if (this.filters.dueDate === 'month') {
                        return task.due_date && this.$date(task.due_date).isSame(today, 'month');
                    } else if (this.filters.dueDate === 'overdue') {
                        return task.due_date && this.$date(task.due_date).isBefore(today, 'day');
                    } else if (this.filters.dueDate === 'none') {
                        return !task.due_date;
                    }
                    return true;
                });
            }

            return filtered;
        },
        totalTasks() {
            return this.teamTasks.length;
        },
        toDoTasks() {
            // Status ID 1 = To Do
            return this.teamTasks.filter(task => task.status_id === 1).length;
        },
        inProgressTasks() {
            // Status ID 2 = In Progress
            return this.teamTasks.filter(task => task.status_id === 2).length;
        },
        blockedTasks() {
            // Status ID 6 = Blocked
            return this.teamTasks.filter(task => task.status_id === 6).length;
        },
        completedTasks() {
            // Status ID 4 = Completed
            return this.teamTasks.filter(task => task.status_id === 4).length;
        },
        roleMembers() {
            const managerIds = this.roleManagers
                ? this.roleManagers().map(m => m.manager_id)
                : [];
            return this.users().filter(
                user => user.role_id === this.roleId && !managerIds.includes(user.id)
            );
        },
        managerUsers() {
            const managerIds = this.roleManagers
                ? this.roleManagers()
                    .filter(m => m.user_role_id === this.roleId)
                    .map(m => m.manager_id)
                : [];
            return this.users().filter(user => managerIds.includes(user.id));
        },
        // Members of this role — available as feedback recipients (excluding the current user)
        teamMemberOptions() {
            const allUsers = (typeof this.users === 'function' ? this.users() : this.users) || [];
            const me = this.get_user;
            return allUsers
                .filter(u => u && u.role_id === this.roleId && (!me || u.id !== me.id))
                .map(u => ({ text: u.name, value: u.id }))
                .sort((a, b) => (a.text || '').localeCompare(b.text || ''));
        },
        totalMembers() {
            return this.roleMembers.length;
        },
    },
    async mounted() {
        this.initializeStatusFilters();
        await this.initializeTeamDetails();
    },
    methods: {
        initializeStatusFilters() {
            // Set default status filters to all statuses except "Completed"
            const statusesList = this.statuses();
            if (Array.isArray(statusesList) && statusesList.length > 0) {
                this.statusFilters = statusesList
                    .filter(status => !status.name.toLowerCase().includes('complet'))
                    .map(status => status.id);
                console.log('Initialized status filters (excluding completed):', this.statusFilters);
            } else {
                console.warn('Statuses not loaded yet, will retry');
                // Retry after a short delay if statuses aren't loaded
                setTimeout(() => {
                    if (this.statusFilters.length === 0) {
                        this.initializeStatusFilters();
                    }
                }, 500);
            }
        },
        async initializeTeamDetails() {
            this.initialLoading = true;
            this.loadingTeam = true;
            this.loadingTasks = true;

            try {
                // Load team info
                await this.loadTeamInfo();
                this.loadingTeam = false;

                // Load tasks
                await this.loadTasks();
                this.loadingTasks = false;

                // Load managers
                await this.loadManagers();

            } catch (error) {
                console.error('Error initializing team details:', error);
                this.$toast.error('Failed to load team details');
            } finally {
                this.initialLoading = false;
            }
        },

        async loadTeamInfo() {
            try {
                const roles = this.get_roles || [];
                const role = roles.find(r => r.id === this.roleId);
                
                if (role) {
                    this.teamName = role.name;
                } else {
                    this.teamName = `Team ${this.roleId}`;
                }
            } catch (error) {
                console.error('Error loading team info:', error);
                throw error;
            }
        },

        async loadTasks() {
            this.loadingTasks = true;
            try {
                const response = await this.$axios.get(`api/tasks/get_tasks_by_role/${this.roleId}`);
                
                if (response.data.response) {
                    this.teamTasks = response.data.data || [];
                    console.log('Loaded team tasks:', this.teamTasks.length);
                } else {
                    this.$toast.error(response.data.message || 'Failed to load tasks');
                }
            } catch (error) {
                console.error('Error loading tasks:', error);
                this.$toast.error('Failed to load tasks');
            } finally {
                this.loadingTasks = false;
            }
        },

        async loadManagers() {
            try {
                await this.refreshManagers();
                const managers = this.allManagers();
                
                // Filter managers for this role
                this.teamManagers = managers.filter(m => m.user_role_id === this.roleId);
                console.log('Team managers:', this.teamManagers);
            } catch (error) {
                console.error('Error loading managers:', error);
            }
        },

        async refreshTasks() {
            await this.loadTasks();
        },

        async sendOverdueReminders() {
            this.sendingOverdueReminders = true;
            try {
                const response = await this.$axios.post('api/tasks/send_overdue_reminders', {
                    role_id: this.roleId,
                });
                if (response.data.response) {
                    this.$toast.success(response.data.message);
                } else {
                    this.$toast.error(response.data.message || 'Failed to send reminders');
                }
            } catch (error) {
                console.error('Error sending overdue reminders:', error);
                this.$toast.error('Failed to send overdue reminders');
            } finally {
                this.sendingOverdueReminders = false;
            }
        },

        // Give Feedback to Team Member
        openFeedbackDialog() {
            this.feedbackForm = { recipient_id: null, category: 'General', message: '' };
            this.feedbackDialog = true;
        },

        closeFeedbackDialog() {
            if (this.sendingFeedback) return;
            this.feedbackDialog = false;
            this.feedbackForm = { recipient_id: null, category: 'General', message: '' };
        },

        async sendFeedback() {
            const recipientId = this.feedbackForm.recipient_id;
            const message = (this.feedbackForm.message || '').trim();
            if (!recipientId) { this.$toast.error('Please pick a team member'); return; }
            if (!message) { this.$toast.error('Please write your feedback'); return; }
            this.sendingFeedback = true;
            try {
                const res = await this.$axios.post('api/employee-growth/feedback', {
                    recipient_id: recipientId,
                    role_id: this.roleId,
                    category: this.feedbackForm.category || 'General',
                    message,
                    type: 'manager',
                    is_anonymous: false,
                });
                if (res.data && res.data.response) {
                    this.$toast.success('Feedback sent!');
                    this.feedbackDialog = false;
                    this.feedbackForm = { recipient_id: null, category: 'General', message: '' };
                    this.$root.$emit('growth:feedback:sent');
                    this.$root.$emit('growth:refresh');
                } else {
                    this.$toast.error('Failed to send feedback');
                }
            } catch (e) {
                console.error('Error sending feedback:', e);
                this.$toast.error('Failed to send feedback');
            } finally {
                this.sendingFeedback = false;
            }
        },

        openCreateTaskDialog() {
            this.createTaskDialog = true;
        },

        async onTasksCreated() {
            this.createTaskDialog = false;
            await this.refreshTasks();
            this.$toast.success('Tasks created successfully');
        },

        openManagerDialog() {
            this.selectedManagerIds = this.managerUsers.map(m => m.id);
            this.managerDialog = true;
        },

        async saveManagers() {
            try {
                const payload = {
                    role_id: this.roleId,
                    manager_ids: this.selectedManagerIds,
                };

                const response = await this.$axios.post('api/users/assign_role_manager', payload);

                if (response.data.response) {
                    this.$toast.success('Managers assigned successfully');
                    await this.refreshManagers();
                    this.managerDialog = false;
                } else {
                    this.$toast.error(response.data.message || 'Failed to assign managers');
                }
            } catch (error) {
                console.error('Error assigning managers:', error);
                this.$toast.error('Failed to assign managers');
            }
        },

        removeManager(managerId) {
            this.selectedManagerIds = this.selectedManagerIds.filter(id => id !== managerId);
        },

        getTeamColor() {
            const colors = ['primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info'];
            return colors[this.roleId % colors.length];
        },

        getTeamIcon() {
            const icons = [
                'mdi-account-supervisor',
                'mdi-cog',
                'mdi-palette',
                'mdi-code-tags',
                'mdi-briefcase',
                'mdi-bullhorn',
            ];
            return icons[this.roleId % icons.length];
        },

        getInitials(name) {
            if (!name) return 'U';
            return name
                .split(' ')
                .map(part => part[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },

        goBack() {
            this.$router.push('/managers-hub');
        },

        isManager(userId) {
            const roleManagerIds = this.roleManagers ? this.roleManagers().map(m => m.manager_id) : [];
            return roleManagerIds.includes(userId);
        },
    },
};
</script>

<style lang="scss" scoped>
.team-details-container {
    min-width: 1200px;
    max-width: 1600px;
    margin: 0 auto;
    padding: 24px;

    .back-to-overview-btn {
        text-transform: none;
        font-size: 14px;
        font-weight: 500;
    }

    .header-section {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 24px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

        .team-info {
            flex: 1;

            .team-name {
                font-size: 28px;
                font-weight: 600;
                color: #212121;
                margin: 0;
            }

            .team-subtitle {
                font-size: 14px;
                color: #666;
                margin-top: 4px;
            }
        }

        .header-actions {
            display: flex;
            gap: 8px;
        }
    }

    .stats-section {
        margin-bottom: 32px;

        .stats-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;

            .stat-card {
                background: white;
                border-radius: 8px;
                padding: 20px 16px;
                text-align: center;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
                transition: all 0.2s ease;

                &:hover {
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
                }

                .stat-icon {
                    font-size: 32px;
                    margin-bottom: 12px;
                }

                .stat-value {
                    font-size: 32px;
                    font-weight: 600;
                    color: #212121;
                    margin-bottom: 6px;
                    line-height: 1;
                }

                .stat-label {
                    font-size: 13px;
                    color: #757575;
                    font-weight: 400;
                }
            }
        }
    }

    .tasks-section {
        background: white;
        border-radius: 12px;
        padding: 24px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

        .virtual-scroll-container {
            max-height: 70vh;
            overflow-y: auto;
            overflow-x: hidden;
        }

        .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .section-title {
                font-size: 20px;
                font-weight: 600;
                color: #212121;
                margin: 0;
            }

            .task-count-badge {
                background: #E3F2FD;
                color: #1976D2;
                padding: 6px 16px;
                border-radius: 16px;
                font-size: 14px;
                font-weight: 600;
            }
        }

        .empty-state {
            text-align: center;
            padding: 80px 24px;

            .empty-title {
                font-size: 20px;
                font-weight: 600;
                color: #666;
                margin: 16px 0 8px;
            }

            .empty-description {
                font-size: 14px;
                color: #999;
                margin-bottom: 24px;
            }
        }
    }

    .tabs-container {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        border-radius: 12px;
        overflow: hidden;
    }

    .members-section {
        .v-card {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            border-radius: 12px;

            .v-card-title {
                font-size: 18px;
                font-weight: 600;
                color: #212121;
            }

            .v-list-item {
                padding: 12px 16px;

                &:hover {
                    background-color: rgba(0, 0, 0, 0.02);
                }
            }
        }
    }
}

@media (max-width: 1200px) {
    .stats-section .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .team-details-container {
        min-width: auto;
        padding: 16px;
    }

    .header-section .team-header {
        flex-direction: column;
        align-items: flex-start;

        .header-actions {
            margin-top: 16px;
            width: 100%;
            flex-direction: column;

            .v-btn {
                width: 100%;
            }
        }
    }

    .stats-section .stats-grid {
        grid-template-columns: 1fr;
    }
}
</style>
